import type {JSX} from "react";

/**
 * Иконки лендинга.
 *
 * Здесь они инлайновые, а не из lucide-react, как в остальном приложении: размер,
 * толщину и цвет обводки задаёт `styles/landing.css` через `svg { stroke: … }` у
 * каждого блока, поэтому иконка обязана быть без собственных `stroke`/`size`.
 * Компоненты lucide проставляют их пропсами и с такой схемой не сочетаются.
 */

type IconProps = { className?: string };

/** Компонент иконки лендинга: className необязателен, размер задаёт CSS. */
export type LandingIcon = (props: IconProps) => JSX.Element;

/** Галочка — микро-доверие в герое и список «что в отчёте». */
export function CheckIcon({className}: IconProps) {
    return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden>
            <path d="M4 12.5 9 17.5 20 6.5"/>
        </svg>
    );
}

/** Сердце (сплошное) — бейдж «Взаимно» в герое. */
export function HeartSolidIcon({className}: IconProps) {
    return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden>
            <path
                d="M12 21s-7.5-4.7-9.4-9.2C1.1 8.4 3 5 6.4 5c2 0 3.5 1.1 4.4 2.4l1.2 1.6 1.2-1.6C14.1 6.1 15.6 5 17.6 5 21 5 22.9 8.4 21.4 11.8 19.5 16.3 12 21 12 21z"/>
        </svg>
    );
}

/** Сердце (контур) — интересы, взаимная симпатия. */
export function HeartIcon({className}: IconProps) {
    return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden>
            <path
                d="M12 20.5s-7.2-4.4-9-8.7C1.5 8.3 3.3 5 6.6 5c1.9 0 3.4 1 4.2 2.3l1.2 1.5 1.2-1.5C14 6 15.5 5 17.4 5c3.3 0 5.1 3.3 3.6 6.8-1.8 4.3-9 8.7-9 8.7Z"/>
        </svg>
    );
}

/** Щит — безопасность, модерация. */
export function ShieldIcon({className}: IconProps) {
    return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden>
            <path d="M12 3 4.5 6.2v5.1c0 4.4 3.1 8.5 7.5 9.7 4.4-1.2 7.5-5.3 7.5-9.7V6.2L12 3Z"/>
        </svg>
    );
}

/** Щит с галочкой — «живые люди, а не боты». */
export function ShieldCheckIcon({className}: IconProps) {
    return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden>
            <path d="M12 3 4.5 6.2v5.1c0 4.4 3.1 8.5 7.5 9.7 4.4-1.2 7.5-5.3 7.5-9.7V6.2L12 3Z"/>
            <path d="M9 12.2 11.2 14.4 15.4 10.2"/>
        </svg>
    );
}

/** Замок — хранение данных. */
export function LockIcon({className}: IconProps) {
    return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden>
            <rect x="4.5" y="10.5" width="15" height="10" rx="3"/>
            <path d="M8 10.5V7.8a4 4 0 0 1 8 0v2.7"/>
            <path d="M12 14.5v2.2"/>
        </svg>
    );
}

/** Часы — ритм жизни, время рождения. */
export function ClockIcon({className}: IconProps) {
    return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden>
            <circle cx="12" cy="12" r="8.2"/>
            <path d="M12 7.4V12l3 2"/>
        </svg>
    );
}

/** Звёзды — астрология (натальная карта). */
export function StarsIcon({className}: IconProps) {
    return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden>
            <path d="m9 3 1.5 3.7L14 8.2l-3.5 1.5L9 13.4 7.5 9.7 4 8.2l3.5-1.5L9 3Z"/>
            <path d="m17 12.5 1 2.3 2.4 1-2.4 1-1 2.4-1-2.4-2.4-1 2.4-1 1-2.3Z"/>
        </svg>
    );
}

/** Цифры — нумерология (числа даты рождения). */
export function NumbersIcon({className}: IconProps) {
    return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden>
            <path d="M9.5 4 7 20M17 4l-2.5 16"/>
            <path d="M4.5 9h15M3.5 15h15"/>
        </svg>
    );
}

/** Профиль — психологический портрет. */
export function PersonIcon({className}: IconProps) {
    return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden>
            <circle cx="12" cy="8" r="3.6"/>
            <path d="M5 20c.7-3.5 3.6-5.4 7-5.4s6.3 1.9 7 5.4"/>
        </svg>
    );
}

/** Метка на карте — город и расстояние. */
export function PinIcon({className}: IconProps) {
    return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden>
            <path d="M12 21s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11Z"/>
            <circle cx="12" cy="10" r="2.4"/>
        </svg>
    );
}

/** Стрелка вниз в лоток — скачать APK. */
export function DownloadIcon({className}: IconProps) {
    return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden>
            <path d="M12 3v12m0 0 4.5-4.5M12 15l-4.5-4.5M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/>
        </svg>
    );
}

/** Рукопожатие-заменитель: две встречные стрелки — знакомство без неловкости. */
export function HandshakeIcon({className}: IconProps) {
    return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden>
            <path d="M3 9h5l2.5 2.5a2 2 0 0 0 2.8 0L16 9h5"/>
            <path d="M6 9V6.5h4M18 9V6.5h-4"/>
            <path d="M3 9v6a2 2 0 0 0 2 2h1M21 9v6a2 2 0 0 1-2 2h-1"/>
        </svg>
    );
}

/** Искры — подборка мест. */
export function SparkIcon({className}: IconProps) {
    return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden>
            <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M18.4 5.6 17 7M7 17l-1.4 1.4"/>
            <circle cx="12" cy="12" r="3.4"/>
        </svg>
    );
}
